import { FC, useEffect, useState } from 'react';

import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { NonPlannedHour } from 'components/types';

import {
  deleteNonPlannedHoursSelection,
  getNonPlannedHours,
  saveNonPlannedHours,
  updateNonPlannedHour
} from 'RestAPIs/capacity-plan/baseplan';

import { scenarioAtom } from '../atoms';

import { Box } from '@mui/material';

import { showSnackbar } from 'components/shared';

import NonPlannedHoursGrid from './components/NonPlannedHoursGrid';
import type { SaveAndUpdateOptions } from './types';

interface WorkSupplyProps {
  setRefreshTotalsGraph: (refresh: boolean) => void;
}

const WorkSupply: FC<WorkSupplyProps> = ({ setRefreshTotalsGraph }) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);
  const [nonPlannedHours, setNonPlannedHours] = useState<NonPlannedHour[]>([]);
  const [loading, setLoading] = useState(false);
  const { year } = useYearQuarterNavigation();

  useEffect(() => {
    if (scenario) {
      const { scenarioId } = scenario;

      getNonPlannedHours({ scenarioId, year }).then(({ data }) => {
        setNonPlannedHours(data);
      });
    }
  }, [scenario, year]);

  const saveAndUpdate = async (params: SaveAndUpdateOptions) => {
    if (!scenario) {
      return;
    }
    setLoading(true);

    const { req, ...rest } = params;
    const options = {
      scenarioId: scenario.scenarioId,
      ...rest
    };

    let cb;
    if (req === 'POST') {
      cb = saveNonPlannedHours;
    }

    if (req === 'PUT') {
      cb = updateNonPlannedHour;
    }

    if (req === 'DELETE') {
      cb = deleteNonPlannedHoursSelection;
    }

    try {
      await cb(options);
      await getNonPlannedHours({ scenarioId: scenario.scenarioId, year }).then(
        ({ data }) => {
          setNonPlannedHours(data);
        }
      );

      setRefreshTotalsGraph(true);
      showSnackbar({
        severity: 'success',
        message: t('changesSaved')
      });
    } catch (e: any) {
      if (
        e.response.data.error.message ===
        'capacity_plan.scenario.employer_settings.no_cla_found'
      ) {
        showSnackbar({
          severity: 'error',
          message: t(`baseplan.errors.${e.response.data.error.message}`, {
            year
          })
        });
      } else {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
    setLoading(false);
  };

  return (
    <Box component="section">
      {nonPlannedHours && (
        <NonPlannedHoursGrid
          disableActions={scenario?.readOnly}
          nonPlannedHours={nonPlannedHours}
          saveAndUpdate={saveAndUpdate}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default WorkSupply;
